import { useCallback, useRef, useState, useEffect } from 'react';
import { IMessage } from '../../../dataProvider/DataProvider';
import { _dataProvider } from '../../../dataProvider/DataProvider';
import { parseMessagesToGiftedFormat } from '../../../utils/messageParser';
import { getJwtObj } from '../../../SharedCommon/utils';

const MAX_HIDDEN_MESSAGE_PAGES = 10;
const CHAT_MESSAGE_PAGE_SIZE = 15;
export const UNREAD_INDICATOR_MIN_COUNT = 10;

// Collect the userIds of the group's leaders (normalized to numbers for consistent comparison).
const getLeaderUserIds = (info: any): number[] =>
  Array.isArray(info?.users)
    ? info.users
        .filter((u: any) => (u.isGroupLeader === true || u.isGroupLeader === 1) && (u.userId ?? u.id) != null)
        .map((u: any) => Number(u.userId ?? u.id))
    : [];

const getDisplayNameForUser = (user: any, includeLoginId: boolean) => {
  const userWithFallback = {
    ...user,
    displayName: user?.displayName ?? user?.name ?? ''
  };

  return _dataProvider.getDisplayNameForUser(userWithFallback, includeLoginId);
};

const getCurrentUserId = () => {
  const userId = getJwtObj()?.userId;
  return userId === undefined || userId === null || String(userId) === '-1' ? null : userId;
};

const getDisappearingMessagesMinutes = (value: unknown): number => {
  const numValue = Number(value);
  return Number.isFinite(numValue) && numValue > 0 ? numValue : 0;
};

const getMessageTimeMs = (message: any): number | null => {
  const time = Number(message?.time);
  if (Number.isFinite(time) && time > 0) {
    return time > 100000000000 ? time : time * 1000;
  }

  if (!message?.createdAt) {
    return null;
  }

  const createdAt = message.createdAt instanceof Date ? message.createdAt : new Date(message.createdAt);
  const createdAtTime = createdAt.getTime();
  return Number.isFinite(createdAtTime) ? createdAtTime : null;
};

const isMessageExpired = (message: any, disappearingMessages: number, now = Date.now()): boolean => {
  if (disappearingMessages <= 0) {
    return false;
  }

  const messageTimeMs = getMessageTimeMs(message);
  if (!messageTimeMs) {
    return false;
  }

  return messageTimeMs + disappearingMessages * 60 * 1000 <= now;
};

interface IUseMessagesOptions {
  groupId: number;
  groupInfo: any;
  disappearingMessages?: number;
  shareInGroup?: number;
  onTagsLoaded?: (tags: number[]) => void;
}

interface IUseMessagesReturn {
  messages: IMessage[];
  setMessages: React.Dispatch<React.SetStateAction<IMessage[]>>;
  fromId: number;
  setFromId: React.Dispatch<React.SetStateAction<number>>;
  scrollFromId: number;
  setScrollFromId: React.Dispatch<React.SetStateAction<number>>;
  isLoadingEarlier: boolean;
  setIsLoadingEarlier: React.Dispatch<React.SetStateAction<boolean>>;
  mapMessage: (data: any) => IMessage[];
  addMessage: (newmessages: any) => void;
  loadMessages: (from: number) => void;
  onNewMessage: (data: any) => void;
  onDeleteMessage: (message: any) => void;
  addServerHiddenMessage: (messageId: number) => void;
  scrollContainerRef: React.MutableRefObject<HTMLElement | null>;
  loadingMessagesRef: React.MutableRefObject<Set<number>>;
  unreadCount: number;
  firstUnreadMessageId: number | null;
  showUnreadIndicator: boolean;
  dismissUnreadIndicator: () => void;
}

export const isUnreadMessageFromOtherUser = (message: any, currentUserId: unknown, lastReadTime: number) =>
  message?.user?.id !== undefined &&
  message?.user?.id !== null &&
  String(message.user.id) !== String(currentUserId) &&
  Number(message?.time) > lastReadTime;

export const useMessages = ({
  groupId,
  groupInfo,
  disappearingMessages,
  shareInGroup,
  onTagsLoaded
}: IUseMessagesOptions): IUseMessagesReturn => {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [fromId, setFromId] = useState(-1);
  const [scrollFromId, setScrollFromId] = useState(-1);
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<number | null>(null);
  const [showUnreadIndicator, setShowUnreadIndicator] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const loadingMessagesRef = useRef<Set<number>>(new Set());
  const mapMessageRef = useRef<((data: any) => IMessage[]) | null>(null);
  const fetchAbortControllersRef = useRef<Map<number, AbortController>>(new Map());
  const currentUserId = getCurrentUserId();
  const activeGroupIdRef = useRef(groupId);
  const unreadIndicatorDismissedRef = useRef(false);
  // Hidden messages are server-authoritative: the only source of truth is what the server
  // reports as hidden. There is intentionally no client-only hide fallback.
  const serverHiddenMessageIdsRef = useRef<Set<number>>(new Set());
  const disappearingMessagesMinutes = getDisappearingMessagesMinutes(
    disappearingMessages ?? groupInfo?.disappearingMessages
  );

  const filterExpiredMessages = useCallback(
    <T extends any[]>(messageList: T): T => {
      if (disappearingMessagesMinutes <= 0 || !Array.isArray(messageList)) {
        return messageList;
      }

      return messageList.filter((message) => !isMessageExpired(message, disappearingMessagesMinutes)) as T;
    },
    [disappearingMessagesMinutes]
  );

  useEffect(() => {
    activeGroupIdRef.current = groupId;
    unreadIndicatorDismissedRef.current = false;
    setUnreadCount(0);
    setFirstUnreadMessageId(null);
    setShowUnreadIndicator(false);
    return () => {
      activeGroupIdRef.current = -1;
    };
  }, [groupId]);

  const dismissUnreadIndicator = useCallback(() => {
    unreadIndicatorDismissedRef.current = true;
    setShowUnreadIndicator(false);
  }, []);

  const normalizeMessageId = useCallback((messageId: number | string): number => {
    const parsed = parseInt(`${messageId}`, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const removeHiddenMessagesFromView = useCallback(() => {
    setMessages((prevMessages) => prevMessages.filter((message) => !serverHiddenMessageIdsRef.current.has(message.id)));
  }, []);

  const applyHiddenMessageIds = useCallback(
    (ids: any, replace = false) => {
      if (!Array.isArray(ids)) {
        return;
      }
      if (replace) {
        serverHiddenMessageIdsRef.current.clear();
      }
      ids.forEach((id) => {
        const normalizedId = normalizeMessageId(id);
        if (normalizedId) {
          serverHiddenMessageIdsRef.current.add(normalizedId);
        }
      });
      removeHiddenMessagesFromView();
    },
    [normalizeMessageId, removeHiddenMessagesFromView]
  );

  const loadMoreHiddenMessageIds = useCallback(
    async (nextBefore: number) => {
      const requestGroupId = groupId;
      let cursor = nextBefore;
      const seenCursors = new Set<number>();
      const accumulatedIds: any[] = [];
      let pageCount = 0;
      try {
        while (cursor && pageCount < MAX_HIDDEN_MESSAGE_PAGES) {
          if (activeGroupIdRef.current !== requestGroupId) {
            break;
          }
          if (seenCursors.has(cursor)) {
            break;
          }
          seenCursors.add(cursor);
          pageCount += 1;

          const groupMessages = await _dataProvider.getGroupMessage(groupId, `hiddenBefore=${cursor}`);
          if (activeGroupIdRef.current !== requestGroupId) {
            break;
          }
          const hiddenMessageIds = (groupMessages as any)?.hiddenMessageIds;
          if (Array.isArray(hiddenMessageIds)) {
            hiddenMessageIds.forEach((id) => accumulatedIds.push(id));
          }
          const nextCursor = (groupMessages as any)?.hiddenNextBefore;
          if (!(groupMessages as any)?.hiddenHasMore || !nextCursor || nextCursor === cursor) {
            break;
          }
          cursor = nextCursor;
        }
      } catch (error) {
        console.error('Failed to load more hidden message IDs:', error);
      }
      if (accumulatedIds.length > 0 && activeGroupIdRef.current === requestGroupId) {
        applyHiddenMessageIds(accumulatedIds);
      }
    },
    [applyHiddenMessageIds, groupId]
  );

  const handleHiddenMetadata = useCallback(
    (groupMessages: any, replace = false) => {
      applyHiddenMessageIds(groupMessages?.hiddenMessageIds, replace);
      if (groupMessages?.hiddenHasMore && groupMessages?.hiddenNextBefore) {
        loadMoreHiddenMessageIds(groupMessages.hiddenNextBefore);
      }
    },
    [applyHiddenMessageIds, loadMoreHiddenMessageIds]
  );

  // Reset the server-hidden set when switching group or user; the new group's hidden IDs
  // arrive from the server via handleHiddenMetadata.
  useEffect(() => {
    serverHiddenMessageIdsRef.current.clear();
  }, [currentUserId, groupId]);

  const addServerHiddenMessage = useCallback(
    (messageId: number) => {
      const normalizedId = normalizeMessageId(messageId);
      if (!normalizedId) {
        return;
      }
      serverHiddenMessageIdsRef.current.add(normalizedId);
      setMessages((prevMessages) => prevMessages.filter((message) => message.id !== normalizedId));
    },
    [normalizeMessageId]
  );

  // Always reflect the latest groupInfo so async parses (e.g. an in-flight message fetch that
  // resolves after a group switch) read the current group's privacy settings, not the ones
  // captured when the request was kicked off.
  const groupInfoRef = useRef(groupInfo);
  groupInfoRef.current = groupInfo;

  const mapMessage = useCallback(
    (data: any): IMessage[] => {
      if (!data || !Array.isArray(data)) {
        return [];
      }

      const validMessages = data.filter((mmm: any) => {
        if (!(mmm && typeof mmm === 'object' && mmm.messageId && mmm.user && mmm.message)) {
          return false;
        }
        if (isMessageExpired(mmm, disappearingMessagesMinutes)) {
          return false;
        }
        const messageId = normalizeMessageId(mmm.messageId || mmm.id);
        return !messageId || !serverHiddenMessageIdsRef.current.has(messageId);
      });

      const currentGroupInfo = groupInfoRef.current;
      const disableMemberShowList = !!currentGroupInfo?.disableMemberShowList;
      const leaderUserIds = getLeaderUserIds(currentGroupInfo);

      const parsedMessages = parseMessagesToGiftedFormat(validMessages, {
        groupId,
        currentUserId: currentUserId ?? undefined,
        disableMemberShowList,
        leaderUserIds,
        currentUserIsGroupLeader: currentUserId != null && leaderUserIds.includes(Number(currentUserId))
      });

      const messageIdMap = new Map<number | string, any>();
      validMessages.forEach((msg: any) => {
        const msgId = msg.messageId || msg.id;
        if (msgId !== undefined && msgId !== null) {
          messageIdMap.set(msgId, msg);
          const numericMsgId = typeof msgId === 'string' ? parseInt(msgId, 10) : msgId;
          if (!isNaN(numericMsgId) && numericMsgId > 0) {
            messageIdMap.set(numericMsgId, msg);
          }
        }
      });

      const convertedMessages: IMessage[] = parsedMessages
        .filter((msg) => msg !== null && msg !== undefined)
        .map((msg) => {
          let numericId: number;
          if (typeof msg.id === 'string') {
            const parsed = parseInt(msg.id, 10);
            numericId = isNaN(parsed) ? 0 : parsed;
          } else {
            numericId = msg.id;
          }

          const originalMsg = messageIdMap.get(numericId) || messageIdMap.get(msg.id);
          let replyMessageId: number | undefined;
          const originalUser = originalMsg?.user;

          if (originalMsg) {
            replyMessageId =
              originalMsg.replyMessageId ||
              originalMsg.replyTo ||
              originalMsg.replyMsgId ||
              originalMsg.replyToMessageId ||
              originalMsg.replyToId ||
              originalMsg.reply_id ||
              originalMsg.replyId ||
              undefined;
          }

          if (
            replyMessageId !== undefined &&
            replyMessageId !== null &&
            replyMessageId !== -1 &&
            replyMessageId !== 0
          ) {
            replyMessageId = Number(replyMessageId);
            if (isNaN(replyMessageId)) {
              replyMessageId = undefined;
            }
          } else {
            replyMessageId = undefined;
          }

          return {
            ...msg,
            id: numericId,
            user: originalUser
              ? {
                  ...msg.user,
                  displayName: originalUser.displayName ?? originalUser.name,
                  loginId: originalUser.loginId,
                  userId: originalUser.userId ?? originalUser.id,
                  name: getDisplayNameForUser(
                    originalUser,
                    !disableMemberShowList || leaderUserIds.includes(Number(originalUser.userId ?? originalUser.id))
                  )
                }
              : msg.user,
            replyMessageId
          } as IMessage;
        });

      convertedMessages.sort((a, b) => {
        const timeA = (a as any).time || (a.createdAt ? a.createdAt.getTime() : 0) || a.id;
        const timeB = (b as any).time || (b.createdAt ? b.createdAt.getTime() : 0) || b.id;
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        return a.id - b.id;
      });

      return convertedMessages;
    },
    [currentUserId, disappearingMessagesMinutes, groupId, normalizeMessageId]
  );

  mapMessageRef.current = mapMessage;

  const loadMergedForwardMessage = useCallback((messageId: number, attachmentUrl: string) => {
    if (loadingMessagesRef.current.has(messageId)) {
      return;
    }

    loadingMessagesRef.current.add(messageId);

    const abortController = new AbortController();
    fetchAbortControllersRef.current.set(messageId, abortController);

    _dataProvider
      .fetchJsonAttachment(attachmentUrl, abortController.signal)
      .then((data) => {
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid response data format');
        }

        let messagesArray: any[] = [];
        if (Array.isArray(data)) {
          messagesArray = data;
        } else if (data && typeof data === 'object' && Array.isArray(data.messages)) {
          messagesArray = data.messages;
        } else {
          throw new Error('Response data does not contain valid messages array');
        }

        if (!Array.isArray(messagesArray) || messagesArray.length === 0) {
          loadingMessagesRef.current.delete(messageId);
          return;
        }

        if (mapMessageRef.current) {
          try {
            const mappedMessages = mapMessageRef.current(messagesArray);
            if (Array.isArray(mappedMessages) && mappedMessages.length > 0) {
              setMessages((prevMessages) => {
                return prevMessages.map((msg) => {
                  if (msg.id === messageId) {
                    return {
                      ...msg,
                      mergedForwardMessages: mappedMessages
                    } as any;
                  }
                  return msg;
                });
              });
            }
          } catch (_error) {
            // Error mapping merged forward messages
          }
        }
        loadingMessagesRef.current.delete(messageId);
        fetchAbortControllersRef.current.delete(messageId);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          loadingMessagesRef.current.delete(messageId);
          fetchAbortControllersRef.current.delete(messageId);
        }
      });
  }, []);

  useEffect(() => {
    const controllers = fetchAbortControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  // Re-reconcile already-parsed messages when the group's privacy settings change (e.g. groupInfo
  // arrives after the messages on first entry). New messages parsed afterwards already use the
  // current settings via groupInfoRef, so this only needs to react to groupInfo.
  useEffect(() => {
    const disableMemberShowList = !!groupInfo?.disableMemberShowList;
    const leaderUserIds = getLeaderUserIds(groupInfo);
    const currentUserIsGroupLeader = currentUserId !== null && leaderUserIds.includes(Number(currentUserId));

    setMessages((prevMessages) => {
      if (!Array.isArray(prevMessages) || prevMessages.length === 0) {
        return prevMessages;
      }

      let changed = false;
      const nextMessages = prevMessages.map((message: any) => {
        if (!message?.user) {
          return message;
        }

        const includeLoginId = !disableMemberShowList || leaderUserIds.includes(Number(message.user.id));
        const newName = getDisplayNameForUser(message.user, includeLoginId);
        let nextPreCompletedNotice = message.preCompletedNotice;

        if (disableMemberShowList && !currentUserIsGroupLeader && message.preCompletedNotice?.users) {
          let noticeChanged = false;
          const users = message.preCompletedNotice.users.map((user: any) => {
            const canShowName =
              String(user.userId) === String(currentUserId) || leaderUserIds.includes(Number(user.userId));
            if (!canShowName && user.displayName !== null) {
              noticeChanged = true;
              return { ...user, displayName: null };
            }
            return user;
          });
          if (noticeChanged) {
            nextPreCompletedNotice = { ...message.preCompletedNotice, users };
          }
        }

        if (newName !== message.user.name || nextPreCompletedNotice !== message.preCompletedNotice) {
          changed = true;
          return {
            ...message,
            user: newName !== message.user.name ? { ...message.user, name: newName } : message.user,
            preCompletedNotice: nextPreCompletedNotice
          };
        }
        return message;
      });

      return changed ? nextMessages : prevMessages;
    });
  }, [currentUserId, groupInfo]);

  const addMessage = useCallback(
    (newmessages: any) => {
      if (!newmessages || !Array.isArray(newmessages)) {
        return;
      }

      setMessages((prevMessages) => {
        if (!prevMessages || !Array.isArray(prevMessages)) {
          return prevMessages;
        }

        const messageIds = new Set(prevMessages.map((m) => m?.id).filter((id) => id != null));
        const uniqueNewMessages = newmessages.filter((m: IMessage) => m && m.id && !messageIds.has(m.id));

        if (uniqueNewMessages.length > 0) {
          const messageMap = new Map<number, IMessage>();
          prevMessages.forEach((msg) => {
            if (msg && msg.id != null) {
              messageMap.set(msg.id, msg);
            }
          });
          uniqueNewMessages.forEach((msg) => {
            if (msg && msg.id != null) {
              messageMap.set(msg.id, msg);
            }
          });
          const merged = Array.from(messageMap.values());
          merged.sort((a, b) => {
            const timeA = (a as any).time || (a.createdAt ? a.createdAt.getTime() : 0) || a.id;
            const timeB = (b as any).time || (b.createdAt ? b.createdAt.getTime() : 0) || b.id;
            if (timeA !== timeB) {
              return timeA - timeB;
            }
            return a.id - b.id;
          });

          uniqueNewMessages.forEach((msg: IMessage) => {
            const msgAny = msg as any;
            if (
              msgAny.type === 'mergedForward' &&
              msgAny.mergedForwardAttachmentUrl &&
              !msgAny.mergedForwardMessages &&
              msgAny.id
            ) {
              const messageId = typeof msgAny.id === 'string' ? parseInt(msgAny.id, 10) : msgAny.id;
              if (!isNaN(messageId)) {
                setTimeout(() => {
                  loadMergedForwardMessage(messageId, msgAny.mergedForwardAttachmentUrl);
                }, 0);
              }
            }
          });

          return merged;
        }
        return prevMessages;
      });
    },
    [loadMergedForwardMessage]
  );

  const loadMessages = useCallback(
    (from: number) => {
      let queryString = '';
      if (from !== -1) {
        queryString += `&from=${from}`;
      }

      if (from === -1 && onTagsLoaded) {
        queryString += '&getTags=true';
      }

      if (shareInGroup !== undefined) {
        queryString += `&shareInGroups=${encodeURIComponent(JSON.stringify([shareInGroup]))}`;
      }
      _dataProvider
        .getGroupMessage(groupId, queryString.substring(1))
        .then((groupMessages) => {
          if (!groupMessages || typeof groupMessages !== 'object') {
            setIsLoadingEarlier(false);
            return;
          }
          if (from === -1 && onTagsLoaded && Array.isArray((groupMessages as any).tags)) {
            const tags = (groupMessages as any).tags
              .map((tag: any) => parseInt(tag, 10))
              .filter((tag: number) => !isNaN(tag));

            onTagsLoaded(tags);
          }

          handleHiddenMetadata(groupMessages, from === -1);

          const messagesArray = Array.isArray(groupMessages) ? groupMessages : groupMessages.messages;
          if (!Array.isArray(messagesArray)) {
            setIsLoadingEarlier(false);
            return;
          }

          if (!messagesArray.length && from === -1) {
            setMessages([]);
            setIsLoadingEarlier(false);
            return;
          }

          try {
            const newMessages = filterExpiredMessages(mapMessage(messagesArray));

            if (from === -1) {
              setMessages(newMessages);
              setUnreadCount(0);
              setFirstUnreadMessageId(null);
              setShowUnreadIndicator(false);
              const lastReadTime = Number((groupMessages as any).lastReadTime);
              if (Number.isFinite(lastReadTime)) {
                const requestGroupId = groupId;
                const loadUnreadHistory = async () => {
                  let allMessages = newMessages;
                  let receivedMessages = messagesArray;
                  const seenFromIds = new Set<number>();

                  while (activeGroupIdRef.current === requestGroupId) {
                    const unreadMessages = allMessages.filter((message: any) =>
                      isUnreadMessageFromOtherUser(message, currentUserId, Math.max(0, lastReadTime))
                    );
                    setUnreadCount(unreadMessages.length);
                    if (unreadMessages.length >= UNREAD_INDICATOR_MIN_COUNT && !unreadIndicatorDismissedRef.current) {
                      setShowUnreadIndicator(true);
                    }

                    const reachedLastReadTime = receivedMessages.some(
                      (message: any) => Number(message?.time) <= Math.max(0, lastReadTime)
                    );
                    const oldestMessage = receivedMessages[receivedMessages.length - 1];
                    const nextFromId = Number(oldestMessage?.messageId ?? oldestMessage?.id);
                    if (
                      receivedMessages.length < CHAT_MESSAGE_PAGE_SIZE ||
                      reachedLastReadTime ||
                      !Number.isFinite(nextFromId) ||
                      seenFromIds.has(nextFromId)
                    ) {
                      setFirstUnreadMessageId(unreadMessages[0]?.id ?? null);
                      setShowUnreadIndicator(
                        !unreadIndicatorDismissedRef.current && unreadMessages.length >= UNREAD_INDICATOR_MIN_COUNT
                      );
                      return;
                    }

                    seenFromIds.add(nextFromId);
                    const earlierResult = await _dataProvider.getGroupMessage(groupId, `from=${nextFromId}`);
                    if (activeGroupIdRef.current !== requestGroupId) {
                      return;
                    }
                    receivedMessages = Array.isArray(earlierResult) ? earlierResult : earlierResult?.messages;
                    if (!Array.isArray(receivedMessages)) {
                      return;
                    }
                    const earlierMessages = filterExpiredMessages(mapMessage(receivedMessages));
                    const messageMap = new Map<number, IMessage>();
                    [...earlierMessages, ...allMessages].forEach((message) => messageMap.set(message.id, message));
                    allMessages = Array.from(messageMap.values()).sort(
                      (a: any, b: any) => Number(a.time ?? a.id) - Number(b.time ?? b.id)
                    );
                    setMessages((prevMessages) => {
                      const latestMessageMap = new Map<number, IMessage>();
                      [...allMessages, ...prevMessages].forEach((message) => latestMessageMap.set(message.id, message));
                      return Array.from(latestMessageMap.values()).sort(
                        (a: any, b: any) => Number(a.time ?? a.id) - Number(b.time ?? b.id)
                      );
                    });
                  }
                };
                void loadUnreadHistory().catch((error) => {
                  console.error('Failed to load unread message history:', error);
                });
              }
              // For initial load, set loading state immediately
              setIsLoadingEarlier(false);
            } else {
              setMessages((prevMessages) => {
                const messageIds = new Set(prevMessages.map((m) => m?.id).filter((id) => id != null));
                const uniqueNewMessages = newMessages.filter((m: IMessage) => m && m.id && !messageIds.has(m.id));

                if (uniqueNewMessages.length > 0) {
                  const messageMap = new Map<number, IMessage>();
                  prevMessages.forEach((msg) => {
                    if (msg && msg.id != null) {
                      messageMap.set(msg.id, msg);
                    }
                  });
                  uniqueNewMessages.forEach((msg) => {
                    if (msg && msg.id != null) {
                      messageMap.set(msg.id, msg);
                    }
                  });
                  const merged = Array.from(messageMap.values());
                  merged.sort((a, b) => {
                    const timeA = (a as any).time || (a.createdAt ? a.createdAt.getTime() : 0) || a.id;
                    const timeB = (b as any).time || (b.createdAt ? b.createdAt.getTime() : 0) || b.id;
                    if (timeA !== timeB) {
                      return timeA - timeB;
                    }
                    return a.id - b.id;
                  });
                  return merged;
                }
                return prevMessages;
              });

              requestAnimationFrame(() => {
                setIsLoadingEarlier(false);
              });
            }

            // Don't auto-set fromId after loading messages to prevent infinite loop
            // fromId should only be set by loadEarlierMessagesAsync when user manually scrolls to top
            // Reset fromId to -1 after loading to prevent automatic re-loading
            setFromId(-1);
          } catch {
            setIsLoadingEarlier(false);
          }
        })
        .catch(() => {
          setIsLoadingEarlier(false);
        });
    },
    [currentUserId, filterExpiredMessages, groupId, mapMessage, handleHiddenMetadata, shareInGroup, onTagsLoaded]
  );

  const onNewMessage = useCallback(
    (data: any) => {
      if (!data || !Array.isArray(data)) {
        return;
      }

      if (!groupInfo || typeof groupInfo !== 'object') {
        return;
      }

      data.forEach((mmm: any) => {
        if (!mmm || !mmm.user) {
          return;
        }

        if (groupInfo?.users && Array.isArray(groupInfo.users)) {
          const msgUser = groupInfo.users.find((uu: any) => uu.userId === mmm.user.id || uu.id === mmm.user.id);
          if (msgUser) {
            mmm.user.loginId = msgUser.uniqueId ?? msgUser.loginId;
          }
        }
      });
      const newmessages = filterExpiredMessages(mapMessage(data));
      addMessage(newmessages);
    },
    [groupInfo, mapMessage, addMessage, filterExpiredMessages]
  );

  const onDeleteMessage = useCallback(
    (message: any) => {
      if (!message || (!message.id && !message.messageId)) {
        return;
      }

      const messageId = normalizeMessageId(message.id || message.messageId);
      serverHiddenMessageIdsRef.current.delete(messageId);

      setMessages((prevMessages) => {
        return prevMessages.filter((msg) => msg.id !== messageId);
      });
    },
    [normalizeMessageId]
  );

  useEffect(() => {
    messages.forEach((msg: any) => {
      if (msg.type === 'mergedForward' && msg.mergedForwardAttachmentUrl && !msg.mergedForwardMessages && msg.id) {
        const messageId = typeof msg.id === 'string' ? parseInt(msg.id, 10) : msg.id;
        if (!isNaN(messageId) && !loadingMessagesRef.current.has(messageId)) {
          loadMergedForwardMessage(messageId, msg.mergedForwardAttachmentUrl);
        }
      }
    });
  }, [messages, loadMergedForwardMessage]);

  useEffect(() => {
    if (disappearingMessagesMinutes <= 0) {
      return;
    }

    const now = Date.now();
    let hasExpiredMessages = false;
    let nextExpirationDelay: number | undefined;

    messages.forEach((message) => {
      const messageTimeMs = getMessageTimeMs(message);
      if (!messageTimeMs) {
        return;
      }

      const delay = messageTimeMs + disappearingMessagesMinutes * 60 * 1000 - now;
      if (delay <= 0) {
        hasExpiredMessages = true;
        return;
      }

      nextExpirationDelay = nextExpirationDelay === undefined ? delay : Math.min(nextExpirationDelay, delay);
    });

    if (hasExpiredMessages) {
      setMessages((prevMessages) => filterExpiredMessages(prevMessages));
    }

    if (nextExpirationDelay === undefined) {
      return;
    }

    const timeoutId = window.setTimeout(
      () => {
        setMessages((prevMessages) => filterExpiredMessages(prevMessages));
      },
      Math.min(nextExpirationDelay + 1000, 2147483647)
    );

    return () => window.clearTimeout(timeoutId);
  }, [disappearingMessagesMinutes, filterExpiredMessages, messages]);

  return {
    messages,
    setMessages,
    fromId,
    setFromId,
    scrollFromId,
    setScrollFromId,
    isLoadingEarlier,
    setIsLoadingEarlier,
    mapMessage,
    addMessage,
    loadMessages,
    onNewMessage,
    onDeleteMessage,
    addServerHiddenMessage,
    scrollContainerRef,
    loadingMessagesRef,
    unreadCount,
    firstUnreadMessageId,
    showUnreadIndicator,
    dismissUnreadIndicator
  };
};
